import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Queue from "@/pages/Queue";
import Support from "@/pages/Support";
import Dashboard from "@/pages/Dashboard";
import Canvas from "@/pages/Canvas";
import TripRequests from "@/pages/TripRequests";
import Quotes from "@/pages/Quotes";
import Bookings from "@/pages/Bookings";
import Compliance from "@/pages/Compliance";
import HermesRuntime from "@/pages/HermesRuntime";
import Adapters from "@/pages/Adapters";
import AuditLog from "@/pages/AuditLog";
import Clients from "@/pages/Clients";
import EmptyLegs, { PublicEmptyLegs, PublicEmptyLegDetail } from "@/pages/EmptyLegs";
import Connections from "@/pages/Connections";
import Prospects from "@/pages/Prospects";
import Strategist from "@/pages/Strategist";
import Widget, { PublicQuote } from "@/pages/Widget";
import { getToken } from "@/lib/api";

function Private({ children }) {
  if (!getToken()) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster
        theme="light"
        position="top-right"
        toastOptions={{
          style: {
            background: "rgb(252,249,243)",
            border: "1px solid rgb(223,214,198)",
            color: "rgb(38,31,24)",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 12,
            borderRadius: 2,
          },
        }}
      />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/widget" element={<Widget />} />
        {/* Public self-serve quote page — the /q/{token} link in quote emails. */}
        <Route path="/q/:token" element={<PublicQuote />} />
        {/* Public empty-leg marketplace — the sitemap/llms.txt front door
            (cards + reserve + price alerts; ops inventory moved to /ops/empty-legs). */}
        <Route path="/empty-legs" element={<PublicEmptyLegs />} />
        <Route path="/empty-legs/:slug" element={<PublicEmptyLegDetail />} />
        {/* The work queue is the default ops landing (AppShell badge mirrors it). */}
        <Route
          path="/"
          element={
            <Private>
              <Queue />
            </Private>
          }
        />
        <Route
          path="/queue"
          element={
            <Private>
              <Queue />
            </Private>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Private>
              <Dashboard />
            </Private>
          }
        />
        <Route
          path="/support"
          element={
            <Private>
              <Support />
            </Private>
          }
        />
        <Route
          path="/canvas"
          element={
            <Private>
              <Canvas />
            </Private>
          }
        />
        <Route
          path="/trips"
          element={
            <Private>
              <TripRequests />
            </Private>
          }
        />
        <Route
          path="/quotes"
          element={
            <Private>
              <Quotes />
            </Private>
          }
        />
        <Route
          path="/bookings"
          element={
            <Private>
              <Bookings />
            </Private>
          }
        />
        <Route
          path="/compliance"
          element={
            <Private>
              <Compliance />
            </Private>
          }
        />
        <Route
          path="/hermes"
          element={
            <Private>
              <HermesRuntime />
            </Private>
          }
        />
        <Route
          path="/adapters"
          element={
            <Private>
              <Adapters />
            </Private>
          }
        />
        <Route
          path="/audit"
          element={
            <Private>
              <AuditLog />
            </Private>
          }
        />
        <Route
          path="/clients"
          element={
            <Private>
              <Clients />
            </Private>
          }
        />
        <Route
          path="/ops/empty-legs"
          element={
            <Private>
              <EmptyLegs />
            </Private>
          }
        />
        {/* Owner OAuth connections — click-to-connect for the social/email
            providers (also the OAuth callback landing: ?connected= / ?error=). */}
        <Route
          path="/ops/connections"
          element={
            <Private>
              <Connections />
            </Private>
          }
        />
        {/* Sales Navigator prospect import — paste/upload a manually exported
            list into compliant leads (provenance salesnav_export). Never sends,
            never scrapes; LinkedIn handles flow into /ops/social as drafts. */}
        <Route
          path="/ops/prospects"
          element={
            <Private>
              <Prospects />
            </Private>
          }
        />
        {/* STRATEGIST — recently generated per-person engagement-strategy briefs.
            Advisory only, human-executed, BACK_STAGE_ONLY. */}
        <Route
          path="/ops/strategist"
          element={
            <Private>
              <Strategist />
            </Private>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
